import React, { useState } from 'react';
import { X, MapPin, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';

export default function StatusModal({ board, status, onClose, onConfirm }) {
  const [notes, setNotes] = useState('');
  const [lostDate, setLostDate] = useState(new Date().toISOString().split('T')[0]);
  const [lostSpot, setLostSpot] = useState('');
  const [loading, setLoading] = useState(false);

  const isLost = status === 'lost';

  const cfg = isLost
    ? { title: 'Report Board Lost', desc: 'Your board will be listed in the community Lost & Found so others can help locate it.', btn: 'Report Lost', color: 'bg-red-500 text-white' }
    : { title: 'Mark as Recycled', desc: 'This board will be marked as recycled / retired and moved to archive.', btn: 'Mark Recycled', color: 'bg-purple-500 text-white' };

  const handleSubmit = async () => {
    if (isLost && !lostSpot.trim()) return;
    setLoading(true);
    await onConfirm(board.board_id, status, notes.trim(), isLost ? lostDate : null, isLost ? lostSpot.trim() : null);
    setLoading(false);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm bg-bg-paper border border-white/10 rounded-2xl p-5 shadow-2xl"
        data-testid="status-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-sm font-semibold">{cfg.title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full"><X className="w-4 h-4 text-muted-foreground" /></button>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">{cfg.desc}</p>
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-3">
          <p className="text-xs font-medium">{board.name}</p>
          <p className="text-[9px] font-mono text-muted-foreground/40">{board.serial}</p>
        </div>

        {/* Lost-specific fields */}
        {isLost && (
          <div className="space-y-2 mb-3">
            <div>
              <label className="text-[10px] text-muted-foreground/60 flex items-center gap-1 mb-1">
                <Calendar className="w-3 h-3" /> When was it lost?
              </label>
              <input
                data-testid="lost-date-input"
                type="date"
                value={lostDate}
                onChange={e => setLostDate(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-red-500/50 [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground/60 flex items-center gap-1 mb-1">
                <MapPin className="w-3 h-3" /> Where was it lost? *
              </label>
              <input
                data-testid="lost-spot-input"
                type="text"
                value={lostSpot}
                onChange={e => setLostSpot(e.target.value)}
                placeholder="Beach / spot name (e.g. Pipeline, Trestles)"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-red-500/50 placeholder:text-muted-foreground/40"
              />
            </div>
          </div>
        )}

        <input
          data-testid="status-notes-input"
          type="text"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={isLost ? "Additional details (what it looks like, distinguishing marks...)" : "reason / notes (optional)"}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40 mb-4"
        />
        <div className="flex gap-2">
          <button
            data-testid="status-confirm-btn"
            onClick={handleSubmit}
            disabled={loading || (isLost && !lostSpot.trim())}
            className={`flex-1 text-xs font-bold py-2.5 rounded-xl transition-opacity ${cfg.color} ${(loading || (isLost && !lostSpot.trim())) ? 'opacity-50' : 'hover:opacity-90'}`}
          >
            {loading ? 'Processing...' : cfg.btn}
          </button>
          <button onClick={onClose} className="px-4 bg-white/5 border border-white/10 text-muted-foreground text-xs py-2.5 rounded-xl hover:border-white/20">Cancel</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
